<!-- webfeed debugger component -->
<template>
  <div class="card dashboard-card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <div>
        <div class="title-row">
          <h4 class="mb-0">Webfeed Debugger</h4>

          <info-box class="info" :leftOffset="-10">
            <div class="info-content">
              <div class="info-title">What this card shows</div>
              <ul class="info-list">
                <li>
                  Webfeeds as seen on the client website (left) and as developed
                  locally (right),
                </li>
                <li>
                  Used to debug any differences that may occur when the webfeed
                  is present on an external site.
                </li>
                <li>
                  <strong>Note:</strong> Some client websites may not allow
                  previews on the dashboard due to security settings.
                  <strong>Click the 'Open' button</strong>
                  to open the website feed in a separate tab to compare feeds.
                </li>
              </ul>
            </div>
          </info-box>
        </div>

        <small class="text-muted">Client feed vs Local feed</small>
      </div>

      <div class="d-flex gap-2">
        <button
          class="btn btn-sm btn-sr btn-sr--secondary touch-btn"
          @click="refresh"
          :disabled="isLoading"
        >
          Refresh
        </button>
      </div>
    </div>

    <div class="card-body">
      <div class="split">
        <!-- LEFT: Client website embed using iframes -->
        <div class="pane">
          <div class="pane__header">
            <div>
              <div class="pane__title">Client Website</div>
              <div class="pane__sub" :title="clientUrl">{{ clientUrl }}</div>
            </div>
            <button
              class="btn btn-sm btn-sr btn-sr--primary touch-btn"
              @click="openClient"
            >
              Open
            </button>
          </div>

          <div class="frame">
            <!-- PRETTY reveal overlay -->
            <div
              v-if="!revealClient"
              class="reveal-overlay"
              @click="reveal('client')"
              role="button"
              tabindex="0"
            >
              <div class="reveal-card">
                <div class="reveal-brand">SENSOREAL</div>
                <div class="reveal-title">Client Website Preview</div>
                <div class="reveal-sub">Click to load the live client feed</div>
                <div class="reveal-cta">Click to preview</div>
              </div>
            </div>

            <!-- existing loading overlay (only makes sense after reveal) -->
            <div v-if="revealClient && loadingClient" class="loading-overlay">
              <div class="spinner"></div>
              <div class="loading-text">Loading client webfeed…</div>
            </div>

            <!-- iframe only rendered after reveal -->
            <iframe
              v-if="revealClient"
              class="iframe"
              :src="clientIframeSrc"
              @load="onClientLoad"
              allow="fullscreen"
              loading="lazy"
              referrerpolicy="no-referrer"
            ></iframe>

            <!-- hide button
            <button
              v-if="revealClient"
              class="btn btn-sm btn-outline-light hide-btn"
              type="button"
              @click.stop="hide('client')"
            >
              Hide
            </button> -->
          </div>
        </div>

        <!-- RIGHT: Canonical embed (webconnect.js initPropertyWebfeed) -->
        <div class="pane">
          <div class="pane__header">
            <div>
              <div class="pane__title">Local embed (Webconnect)</div>
              <div class="pane__sub">
                Feed injected via webconnect from assets server
              </div>
            </div>
          </div>

          <div class="frame frame--light">
            <!-- PRETTY reveal overlay -->
            <div
              v-if="!revealCanonical"
              class="reveal-overlay reveal-overlay--light"
              @click="reveal('canonical')"
              role="button"
              tabindex="0"
            >
              <div class="reveal-card">
                <div class="reveal-brand">SENSOREAL</div>
                <div class="reveal-title">Local Webconnect Preview</div>
                <div class="reveal-sub">
                  Click to load the local embedded feed
                </div>
                <div class="reveal-cta reveal-cta--dark">Click to preview</div>
              </div>
            </div>

            <!-- existing loading overlay (only after reveal) -->
            <div
              v-if="revealCanonical && loadingCanonical"
              class="loading-overlay loading-overlay--light"
            >
              <div class="spinner spinner--dark"></div>
              <div class="loading-text loading-text--dark">
                Loading local webfeed…
              </div>
            </div>

            <!-- webconnect mounts only after reveal -->
            <div v-if="revealCanonical" class="feed-viewport">
              <div
                id="feed"
                ref="feedRoot"
                class="feed-root"
                :key="refreshKey"
              ></div>
            </div>

            <!-- hide button
            <button
              v-if="revealCanonical"
              class="btn btn-sm btn-outline-secondary hide-btn hide-btn--dark"
              type="button"
              @click.stop="hide('canonical')"
            >
              Hide
            </button> -->
          </div>
        </div>
      </div>

      <div class="text-muted mt-2 small"></div>
    </div>
  </div>
</template>

<script>
import InfoBox from "@/components/InfoBox";
const ASSETS_HOST = (process.env.VUE_APP_ASSETS_HOST || "").replace(/\/$/, "");
const WEBCONNECT_JS = `${ASSETS_HOST}/webconnect.js`;

export default {
  name: "DashboardWebfeedDebugger",
  components: { InfoBox },

  props: {
    clientUrl: { type: String, required: true }
  },

  data() {
    return {
      refreshKey: 0,
      loadingClient: true,
      loadingCanonical: true,
      revealClient: false,
      revealCanonical: false
    };
  },

  computed: {
    isLoading() {
      return (
        (this.revealClient && this.loadingClient) ||
        (this.revealCanonical && this.loadingCanonical)
      );
    },

    clientIframeSrc() {
      return this.cacheBustUrl(this.clientUrl);
    }
  },

  watch: {
    refreshKey() {
      if (this.revealClient) this.loadingClient = true;
      if (this.revealCanonical) this.loadingCanonical = true;
    },

    clientUrl() {
      this.loadingClient = true;
    }
  },

  //   mounted() {
  //     this.initCanonicalEmbed();
  //   },

  methods: {
    refresh() {
      this.refreshKey += 1;

      // reset loaders ONLY for revealed panes
      if (this.revealClient) this.loadingClient = true;
      if (this.revealCanonical) this.loadingCanonical = true;

      this.$nextTick(() => {
        // only re-init canonical if it’s currently visible
        if (this.revealCanonical) {
          this.initCanonicalEmbed();

          // Safety retry if embed mounts slowly / fails first pass
          setTimeout(() => {
            const root = this.$refs.feedRoot;
            if (root && root.children && root.children.length === 0) {
              this.initCanonicalEmbed();
            }
          }, 600);
        }
      });
    },

    onClientLoad() {
      this.loadingClient = false;
    },

    openClient() {
      window.open(this.clientUrl, "_blank", "noopener,noreferrer");
    },

    cacheBustUrl(url) {
      // ensure dbgRefresh is added BEFORE the hash so the browser actually reloads the iframe
      const parts = url.split("#");
      const base = parts[0];
      const hash = parts.length > 1 ? "#" + parts.slice(1).join("#") : "";
      const joiner = base.indexOf("?") === -1 ? "?" : "&";
      return `${base}${joiner}dbgRefresh=${this.refreshKey}${hash}`;
    },

    reloadWebconnectScript() {
      return new Promise((resolve, reject) => {
        // Remove ANY existing webconnect runtimes (old projects, cache-busted versions, etc)
        document
          .querySelectorAll('script[src*="/webconnect.js"]')
          .forEach(s => s.remove());

        // Clear global hook
        try {
          delete window.initPropertyWebfeed;
        } catch (e) {}
        window.initPropertyWebfeed = undefined;

        const s = document.createElement("script");
        s.src = WEBCONNECT_JS + "?t=" + Date.now();
        s.async = true;
        s.defer = true;

        s.onload = () => {
          if (window.initPropertyWebfeed) resolve();
          else
            reject(
              new Error("webconnect.js loaded but initPropertyWebfeed missing")
            );
        };

        s.onerror = () => reject(new Error(`Failed to load ${WEBCONNECT_JS}`));

        document.body.appendChild(s);
      });
    },

    async initCanonicalEmbed() {
      try {
        this.loadingCanonical = true;

        // clear mount point
        const root = this.$refs.feedRoot;
        if (root) root.innerHTML = "";

        // force reload the embed runtime so it behaves like a browser refresh
        await this.reloadWebconnectScript();

        // init into constant container
        window.initPropertyWebfeed("#feed", this.$config.webfeedConfig);

        // turn off loader once it actually mounts (or timeout)
        const start = Date.now();
        const check = setInterval(() => {
          const r = this.$refs.feedRoot;
          if (r && r.querySelector("#sensoreal")) {
            clearInterval(check);
            this.loadingCanonical = false;
          } else if (Date.now() - start > 8000) {
            clearInterval(check);
            this.loadingCanonical = false;
          }
        }, 100);
      } catch (e) {
        console.error(e);
        this.loadingCanonical = false;
      }
    },
    reveal(which) {
      if (which === "client") {
        this.revealClient = true;
        // iframe load event will flip loadingClient false
      }

      if (which === "canonical") {
        this.revealCanonical = true;

        // init embed only when it becomes visible
        this.$nextTick(() => {
          this.initCanonicalEmbed();
        });
      }
    },

    hide(which) {
      if (which === "client") this.revealClient = false;
      if (which === "canonical") this.revealCanonical = false;
    }
  }
};
</script>

<style scoped lang="scss">
.title-row {
  display: flex;
  align-items: center;
  gap: 9px;
}

.split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 992px) {
  .split {
    grid-template-columns: 1fr;
  }
}

.pane {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.pane__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px 10px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.pane__title {
  font-weight: 600;
  font-size: 13px;
  color: #111827;
}

.pane__sub {
  font-size: 12px;
  color: #6b7280;
  max-width: 520px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.frame {
  position: relative;
  height: 520px;
  background: #0b0b0b;
}

.frame--light {
  background: #ffffff;
}

.iframe {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}

/* canonical mount container */
.feed-viewport {
  width: 100%;
  height: 100%;
  overflow: scroll;
  background: #fff;
}

.feed-root {
  width: 125%; /* compensate for scale so it fills the viewport */
  height: 125%;
  transform: scale(0.8);
  transform-origin: top left;
}

/* loading overlay */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10, 10, 10, 0.55);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.loading-overlay--light {
  background: rgba(255, 255, 255, 0.75);
}

.loading-text {
  margin-top: 10px;
  color: #fff;
  font-size: 13px;
  opacity: 0.9;
}

.loading-text--dark {
  color: #111827;
}

.spinner {
  width: 26px;
  height: 26px;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

.spinner--dark {
  border: 3px solid rgba(17, 24, 39, 0.15);
  border-top-color: rgba(17, 24, 39, 0.75);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== preview overlay ===== */
.reveal-overlay {
  position: absolute;
  inset: 0;
  z-index: 6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  /* dark/nav vibe */
  background: linear-gradient(
    135deg,
    rgba(12, 18, 28, 0.92),
    rgba(10, 16, 26, 0.92)
  );

  /* subtle vignette */
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
}

.reveal-overlay--light {
  /* still keep it “nav vibe” but slightly lighter so it feels distinct */
  background: linear-gradient(
    135deg,
    rgba(16, 24, 39, 0.78),
    rgba(12, 18, 28, 0.72)
  );
}

.reveal-card {
  width: 86%;
  max-width: 360px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 18px 16px;
  border-radius: 10px;

  /* nav panel feel */
  background: rgba(17, 24, 39, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);

  transform: translateY(0);
  transition: transform 220ms ease, box-shadow 220ms ease,
    background-color 220ms ease;

  position: relative;
}

.reveal-card:before {
  /* thin divider line like the nav */
  content: "";
  position: absolute;
  left: 18px;
  right: 18px;
  top: 14px;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  opacity: 0.9;
}

.reveal-overlay:hover .reveal-card {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35), 0 2px 6px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.reveal-title {
  font-weight: 700;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.2px;
  margin-top: 10px;
}

.reveal-sub {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.62);
  line-height: 1.4;
}

.reveal-brand {
  font-size: 11px;
  letter-spacing: 3px;
  color: rgba(255, 255, 255, 0.38);
}

/* CTA styled like a nav “active” accent */
.reveal-cta {
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;

  padding: 8px 12px;
  border-radius: 8px;

  color: #26c281; /* nav green vibe */
  border: 1px solid rgba(38, 194, 129, 0.35);
  background: rgba(38, 194, 129, 0.08);
}

.reveal-overlay:hover .reveal-cta {
  background: rgba(38, 194, 129, 0.12);
  border-color: rgba(38, 194, 129, 0.55);
  box-shadow: 0 0 0 3px rgba(38, 194, 129, 0.08);
}

/* If you keep reveal-cta--dark in markup, map it to same theme */
.reveal-cta--dark {
  color: #26c281;
  border: 1px solid rgba(38, 194, 129, 0.35);
  background: rgba(38, 194, 129, 0.08);
}
</style>

<style lang="scss">
.info .info-box__btn {
  position: relative;
  top: 2px;
}
</style>
